/**
 * @page FlexLandingPage
 * @route /jobs (public, no auth)
 * @description Landing Page für Flex-Worker Bewerbungen.
 *   Mobile-First, eigenständiges Design mit WeShip Branding.
 *   Traffic kommt großteils von Social-Media Ads (Instagram/Facebook).
 *   i18n-fähig (DE/EN), YouTube-Video, Standortfoto, Google Maps.
 */
import { useState, useRef, useEffect } from 'react';
import { MapPin, Clock, Users, GraduationCap, Dumbbell, Euro, Upload, X, CheckCircle, ChevronRight, Phone, Mail, FileText, Menu, XIcon, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';
const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

export default function FlexLandingPage() {
  const { t, language, setLanguage } = useLanguage();
  useEffect(() => { if (language !== 'de') setLanguage('de'); }, []); // DE als Standard
  const formRef = useRef(null);
  const benefitsRef = useRef(null);
  const stepsRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Landing config from backend
  const [config, setConfig] = useState(null);

  // Form state
  const [form, setForm] = useState({
    vorname: '', nachname: '', email: '', telefon: '', geburtsdatum: '',
    fruehest_verfuegbar: '', motivation: '', quelle: '',
  });
  const [verfuegbarkeit, setVerfuegbarkeit] = useState([]);
  const [cv, setCv] = useState(null);
  const [cvLater, setCvLater] = useState(false);
  const [datenschutz, setDatenschutz] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState('');
  const [standortImgOk, setStandortImgOk] = useState(false);

  // Fetch landing config
  useEffect(() => {
    fetch(`${API}/api/public/flex/landing-config`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setConfig(d))
      .catch(() => {});
  }, []);

  // Check if standort image exists
  useEffect(() => {
    const img = new Image();
    img.onload = () => setStandortImgOk(true);
    img.onerror = () => setStandortImgOk(false);
    img.src = '/assets/weship-standort.jpg';
  }, []);

  const scrollTo = (ref) => {
    setMobileMenuOpen(false);
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleDay = (day) => {
    setVerfuegbarkeit(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const validate = () => {
    const e = {};
    if (!form.vorname.trim()) e.vorname = t('flex.landing.required');
    if (!form.nachname.trim()) e.nachname = t('flex.landing.required');
    if (!form.email.trim()) e.email = t('flex.landing.required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t('flex.landing.invalidEmail');
    if (!form.telefon.trim()) e.telefon = t('flex.landing.required');
    else if (!/^[+\d\s\-()]{6,20}$/.test(form.telefon)) e.telefon = t('flex.landing.invalidPhone');
    if (!form.geburtsdatum) e.geburtsdatum = t('flex.landing.required');
    if (cv) {
      if (cv.size > 5 * 1024 * 1024) e.cv = t('flex.landing.cvMaxSize');
      else if (!cv.name.toLowerCase().endsWith('.pdf')) e.cv = t('flex.landing.cvPdfOnly');
    }
    if (!datenschutz) e.datenschutz = t('flex.landing.privacyRequired');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setServerError('');

    try {
      const fd = new FormData();
      fd.append('vorname', form.vorname.trim());
      fd.append('nachname', form.nachname.trim());
      fd.append('email', form.email.trim());
      fd.append('telefon', form.telefon.trim());
      fd.append('geburtsdatum', form.geburtsdatum);
      fd.append('quelle', form.quelle || 'landing_page');
      if (verfuegbarkeit.length > 0) fd.append('verfuegbarkeit', verfuegbarkeit.join(','));
      if (form.fruehest_verfuegbar) fd.append('fruehest_verfuegbar', form.fruehest_verfuegbar);
      if (form.motivation.trim()) fd.append('motivation', form.motivation.trim());
      if (cv) fd.append('lebenslauf', cv);
      if (cvLater) fd.append('cv_later', 'true');

      const res = await fetch(`${API}/api/public/flex/bewerben`, { method: 'POST', body: fd });
      const data = await res.json();

      if (!res.ok) {
        setServerError(data.error || t('flex.landing.errorGeneral'));
        return;
      }

      setSubmitted(true);
    } catch {
      setServerError(t('flex.landing.errorConnection'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        setErrors(prev => ({ ...prev, cv: t('flex.landing.cvPdfOnly') }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, cv: t('flex.landing.cvMaxSize') }));
        return;
      }
      setCv(file);
      setErrors(prev => { const { cv: _, ...rest } = prev; return rest; });
    }
  };

  const inputCls = (field) =>
    `w-full px-4 py-3 rounded-xl border ${errors[field] ? 'border-red-400 bg-red-50' : 'border-[var(--border-default,#E5E7EB)] bg-white'} text-[#1A1A2E] placeholder-gray-400 focus:outline-none focus:border-[#338edc] focus:ring-2 focus:ring-[#338edc]/20 transition-colors text-[16px]`;

  const benefits = [
    { icon: Euro, titleKey: 'flex.landing.benefit1Title', descKey: 'flex.landing.benefit1Desc' },
    { icon: Clock, titleKey: 'flex.landing.benefit2Title', descKey: 'flex.landing.benefit2Desc' },
    { icon: Users, titleKey: 'flex.landing.benefit3Title', descKey: 'flex.landing.benefit3Desc' },
    { icon: GraduationCap, titleKey: 'flex.landing.benefit4Title', descKey: 'flex.landing.benefit4Desc' },
    { icon: Dumbbell, titleKey: 'flex.landing.benefit5Title', descKey: 'flex.landing.benefit5Desc' },
    { icon: MapPin, titleKey: 'flex.landing.benefit6Title', descKey: 'flex.landing.benefit6Desc' },
  ];

  const steps = [
    { num: '1', titleKey: 'flex.landing.step1Title', descKey: 'flex.landing.step1Desc' },
    { num: '2', titleKey: 'flex.landing.step2Title', descKey: 'flex.landing.step2Desc' },
    { num: '3', titleKey: 'flex.landing.step3Title', descKey: 'flex.landing.step3Desc' },
    { num: '4', titleKey: 'flex.landing.step4Title', descKey: 'flex.landing.step4Desc' },
  ];

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>

      {/* ─── Sticky Nav ─── */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-[var(--border-table,#F3F4F6)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <img src="/weship-logo.png" alt="WeShip" className="h-8" />

          {/* Desktop Nav */}
          <div className="hidden sm:flex items-center gap-6">
            <button onClick={() => scrollTo(benefitsRef)} className="text-sm text-[#6B7280] hover:text-[#1A1A2E] transition-colors">
              {t('flex.landing.navAbout')}
            </button>
            <button onClick={() => scrollTo(stepsRef)} className="text-sm text-[#6B7280] hover:text-[#1A1A2E] transition-colors">
              {t('flex.landing.navSteps')}
            </button>
            <button onClick={() => scrollTo(formRef)} className="text-sm font-semibold text-white bg-[#338edc] hover:bg-[#2b7bc4] px-5 py-2 rounded-xl transition-colors">
              {t('flex.landing.navApply')}
            </button>

            {/* Language Toggle */}
            <div className="flex items-center gap-1.5 text-sm">
              <Globe size={14} className="text-[#9CA3AF]" />
              <button
                className={`px-1 ${language === 'de' ? 'font-semibold text-[#338edc]' : 'text-[#9CA3AF] hover:text-[#6B7280]'}`}
                onClick={() => setLanguage('de')}
              >DE</button>
              <span className="text-[#D1D5DB]">|</span>
              <button
                className={`px-1 ${language === 'en' ? 'font-semibold text-[#338edc]' : 'text-[#9CA3AF] hover:text-[#6B7280]'}`}
                onClick={() => setLanguage('en')}
              >EN</button>
            </div>
          </div>

          {/* Mobile: Language + Burger */}
          <div className="flex sm:hidden items-center gap-3">
            <div className="flex items-center gap-1 text-xs">
              <button
                className={`px-1 ${language === 'de' ? 'font-semibold text-[#338edc]' : 'text-[#9CA3AF]'}`}
                onClick={() => setLanguage('de')}
              >DE</button>
              <span className="text-[#D1D5DB]">|</span>
              <button
                className={`px-1 ${language === 'en' ? 'font-semibold text-[#338edc]' : 'text-[#9CA3AF]'}`}
                onClick={() => setLanguage('en')}
              >EN</button>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-[#6B7280]">
              {mobileMenuOpen ? <XIcon size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-[var(--border-table,#F3F4F6)] bg-white px-4 py-3 space-y-2">
            <button onClick={() => scrollTo(benefitsRef)} className="block w-full text-left py-2 text-[#6B7280]">{t('flex.landing.navAbout')}</button>
            <button onClick={() => scrollTo(stepsRef)} className="block w-full text-left py-2 text-[#6B7280]">{t('flex.landing.navSteps')}</button>
            <button onClick={() => scrollTo(formRef)} className="block w-full text-left py-2 font-semibold text-[#338edc]">{t('flex.landing.navApply')}</button>
          </div>
        )}
      </nav>

      {/* ─── Hero Section ─── */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #338edc 0%, #6366F1 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-white rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-white rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24 lg:py-32 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
            {t('flex.landing.heroTitle')}
          </h1>
          <p className="mt-4 sm:mt-6 text-lg sm:text-xl text-white/85 max-w-xl mx-auto leading-relaxed">
            {t('flex.landing.heroLine1')}<br />
            {t('flex.landing.heroLine2')}
          </p>
          <button
            onClick={() => scrollTo(formRef)}
            className="mt-8 inline-flex items-center gap-2 bg-white text-[#338edc] font-semibold text-lg px-8 py-4 rounded-2xl hover:bg-gray-50 transition-colors shadow-sm shadow-black/10"
          >
            {t('flex.landing.navApply')} <ChevronRight size={20} />
          </button>
        </div>
      </section>

      {/* ─── YouTube Video ─── */}
      {config?.youtube_video_id && (
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A2E] text-center mb-8">
              {t('flex.landing.videoTitle')}
            </h2>
            <div className="relative w-full rounded-xl overflow-hidden shadow-sm" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${config.youtube_video_id}`}
                title="WeShip Flex-Worker"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      {/* ─── Benefits ─── */}
      <section ref={benefitsRef} className="py-16 sm:py-20 bg-[#F8FAFC]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A2E] text-center">{t('flex.landing.benefitsTitle')}</h2>
          <p className="mt-3 text-[#6B7280] text-center max-w-lg mx-auto">{t('flex.landing.benefitsSubtitle')}</p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[var(--border-table,#F3F4F6)] p-6 hover:border-[#338edc]/30 transition-colors text-center sm:text-left">
                <div className="w-12 h-12 rounded-xl bg-[#EBF4FD] flex items-center justify-center mx-auto sm:mx-0">
                  <b.icon size={24} className="text-[#338edc]" />
                </div>
                <h3 className="mt-4 font-semibold text-[#1A1A2E]">{t(b.titleKey)}</h3>
                <p className="mt-1 text-sm text-[#6B7280] leading-relaxed">{t(b.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How it works ─── */}
      <section ref={stepsRef} className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A2E] text-center">{t('flex.landing.stepsTitle')}</h2>
          <p className="mt-3 text-[#6B7280] text-center max-w-lg mx-auto">{t('flex.landing.stepsSubtitle')}</p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="relative text-center sm:text-left">
                <div className="w-10 h-10 rounded-full bg-[#338edc] text-white font-bold text-lg flex items-center justify-center mx-auto sm:mx-0">
                  {s.num}
                </div>
                <h3 className="mt-3 font-semibold text-[#1A1A2E]">{t(s.titleKey)}</h3>
                <p className="mt-1 text-sm text-[#6B7280] leading-relaxed">{t(s.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Standort + Google Maps ─── */}
      {(standortImgOk || config?.standort_adresse) && (
        <section className="py-16 sm:py-20 bg-[#F8FAFC]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A2E] text-center mb-8">
              {t('flex.landing.locationTitle')}
            </h2>

            {standortImgOk && (
              <div className="rounded-xl overflow-hidden">
                <img
                  src="/assets/weship-standort.jpg"
                  alt="WeShip Fulfillment Center"
                  className="w-full h-64 md:h-96 object-cover"
                />
              </div>
            )}

            {config?.standort_adresse && (
              <div className={`rounded-xl overflow-hidden border border-[var(--border-default,#E5E7EB)] ${standortImgOk ? 'mt-6' : ''}`}>
                <iframe
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(config.standort_adresse)}&output=embed`}
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Google Maps"
                />
              </div>
            )}

            {config?.standort_adresse && (
              <div className="text-center mt-4">
                <a
                  href={`https://www.google.com/maps/dir//${encodeURIComponent(config.standort_adresse)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#338edc] hover:underline inline-flex items-center gap-1.5"
                >
                  <MapPin size={14} /> {t('flex.landing.routePlanner')}
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ─── Application Form ─── */}
      <section ref={formRef} className="py-16 sm:py-20 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A2E] text-center">{t('flex.landing.formTitle')}</h2>
          <p className="mt-3 text-[#6B7280] text-center">{t('flex.landing.formSubtitle')}</p>

          {submitted ? (
            <div className="mt-10 bg-white rounded-2xl border border-[var(--border-table,#F3F4F6)] p-8 sm:p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto">
                <CheckCircle size={32} className="text-green-500" />
              </div>
              <h3 className="mt-6 text-xl font-semibold text-[#1A1A2E]">{t('flex.landing.successTitle')}</h3>
              <p className="mt-3 text-[#6B7280] max-w-md mx-auto">
                {t('flex.landing.successMessage')}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-10 bg-[#F8FAFC] rounded-2xl border border-[var(--border-table,#F3F4F6)] p-6 sm:p-8 space-y-5">

              {serverError && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                  {serverError}
                </div>
              )}

              {/* Name row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A1A2E] mb-1.5">{t('flex.landing.firstName')} *</label>
                  <input
                    type="text"
                    value={form.vorname}
                    onChange={e => setForm(f => ({ ...f, vorname: e.target.value }))}
                    placeholder="Max"
                    className={inputCls('vorname')}
                  />
                  {errors.vorname && <p className="mt-1 text-xs text-red-500">{errors.vorname}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1A2E] mb-1.5">{t('flex.landing.lastName')} *</label>
                  <input
                    type="text"
                    value={form.nachname}
                    onChange={e => setForm(f => ({ ...f, nachname: e.target.value }))}
                    placeholder="Mustermann"
                    className={inputCls('nachname')}
                  />
                  {errors.nachname && <p className="mt-1 text-xs text-red-500">{errors.nachname}</p>}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-[#1A1A2E] mb-1.5">{t('flex.landing.email')} *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="max@beispiel.at"
                  className={inputCls('email')}
                />
                {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-[#1A1A2E] mb-1.5">{t('flex.landing.phone')} *</label>
                <input
                  type="tel"
                  value={form.telefon}
                  onChange={e => setForm(f => ({ ...f, telefon: e.target.value }))}
                  placeholder="+43 660 1234567"
                  className={inputCls('telefon')}
                />
                {errors.telefon && <p className="mt-1 text-xs text-red-500">{errors.telefon}</p>}
              </div>

              {/* Birthdate */}
              <div>
                <label className="block text-sm font-medium text-[#1A1A2E] mb-1.5">{t('flex.landing.birthdate')} *</label>
                <input
                  type="date"
                  value={form.geburtsdatum}
                  onChange={e => setForm(f => ({ ...f, geburtsdatum: e.target.value }))}
                  className={inputCls('geburtsdatum')}
                />
                {errors.geburtsdatum && <p className="mt-1 text-xs text-red-500">{errors.geburtsdatum}</p>}
              </div>

              {/* CV Upload */}
              <div>
                <label className="block text-sm font-medium text-[#1A1A2E] mb-1.5">{t('flex.landing.cvLabel')}</label>
                {cv ? (
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[var(--border-default,#E5E7EB)] bg-[#EBF4FD]">
                    <FileText size={20} className="text-[#338edc] flex-shrink-0" />
                    <span className="text-sm text-[#1A1A2E] truncate flex-1">{cv.name}</span>
                    <span className="text-xs text-[#6B7280] flex-shrink-0">{(cv.size / 1024 / 1024).toFixed(1)} MB</span>
                    <button type="button" onClick={() => setCv(null)} className="p-1 text-[#6B7280] hover:text-red-500 transition-colors flex-shrink-0">
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <label className={`flex flex-col items-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed transition-colors ${cvLater ? 'border-[var(--border-table,#F3F4F6)] bg-gray-50 cursor-not-allowed opacity-50' : `cursor-pointer ${errors.cv ? 'border-red-300 bg-red-50' : 'border-[var(--border-default,#E5E7EB)] bg-white hover:border-[#338edc] hover:bg-[#EBF4FD]/50'}`}`}>
                    <Upload size={24} className={cvLater ? 'text-gray-300' : 'text-[#6B7280]'} />
                    <span className={`text-sm ${cvLater ? 'text-gray-300' : 'text-[#6B7280]'}`}>{t('flex.landing.cvUploadHint')}</span>
                    <input type="file" accept=".pdf,application/pdf" onChange={handleFileChange} className="hidden" disabled={cvLater} />
                  </label>
                )}
                {errors.cv && <p className="mt-1 text-xs text-red-500">{errors.cv}</p>}

                {/* CV Later checkbox */}
                <label className="flex items-center gap-2.5 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cvLater}
                    onChange={e => { setCvLater(e.target.checked); if (e.target.checked) setCv(null); }}
                    className="w-4.5 h-4.5 rounded border-[var(--border-default,#E5E7EB)] text-[#338edc] focus:ring-[#338edc]"
                  />
                  <span className="text-sm text-[#6B7280]">{t('flex.landing.cvLater')}</span>
                </label>
                {cvLater && (
                  <p className="mt-1.5 text-xs text-[#338edc]">{t('flex.landing.cvLaterHint')}</p>
                )}
              </div>

              {/* ─── Phase 2 Fields (optional) ─── */}
              <div className="pt-2 border-t border-[var(--border-default,#E5E7EB)]">
                <p className="text-xs text-[#9CA3AF] mb-4">{t('flex.landing.optionalFields')}</p>

                {/* Availability weekdays */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-[#1A1A2E] mb-2">{t('flex.landing.availability')}</label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS.map(tag => (
                      <label key={tag} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border cursor-pointer transition-colors text-sm ${verfuegbarkeit.includes(tag) ? 'bg-[#EBF4FD] border-[#338edc] text-[#338edc] font-medium' : 'bg-white border-[var(--border-default,#E5E7EB)] text-[#6B7280] hover:border-[#338edc]/40'}`}>
                        <input
                          type="checkbox"
                          checked={verfuegbarkeit.includes(tag)}
                          onChange={() => toggleDay(tag)}
                          className="sr-only"
                        />
                        {tag}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Earliest availability */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-[#1A1A2E] mb-1.5">{t('flex.landing.earliestStart')}</label>
                  <input
                    type="date"
                    min={today}
                    value={form.fruehest_verfuegbar}
                    onChange={e => setForm(f => ({ ...f, fruehest_verfuegbar: e.target.value }))}
                    className={inputCls('fruehest_verfuegbar')}
                  />
                </div>

                {/* Motivation */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-[#1A1A2E] mb-1.5">{t('flex.landing.motivation')}</label>
                  <textarea
                    rows={3}
                    maxLength={500}
                    value={form.motivation}
                    onChange={e => setForm(f => ({ ...f, motivation: e.target.value }))}
                    placeholder={t('flex.landing.motivationPlaceholder')}
                    className={`${inputCls('motivation')} resize-none`}
                  />
                  <p className="mt-1 text-xs text-[#9CA3AF] text-right">{form.motivation.length}/500</p>
                </div>

                {/* Source */}
                <div className="mb-1">
                  <label className="block text-sm font-medium text-[#1A1A2E] mb-1.5">{t('flex.landing.source')}</label>
                  <select
                    value={form.quelle}
                    onChange={e => setForm(f => ({ ...f, quelle: e.target.value }))}
                    className={inputCls('quelle')}
                  >
                    <option value="">{t('flex.landing.selectSource')}</option>
                    <option value="instagram">Instagram</option>
                    <option value="facebook">Facebook</option>
                    <option value="freunde">{t('flex.landing.friends')}</option>
                    <option value="google">Google</option>
                    <option value="sonstiges">{t('flex.landing.other')}</option>
                  </select>
                </div>
              </div>

              {/* Privacy checkbox */}
              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={datenschutz}
                    onChange={e => setDatenschutz(e.target.checked)}
                    className="mt-0.5 w-5 h-5 rounded border-[var(--border-default,#E5E7EB)] text-[#338edc] focus:ring-[#338edc] flex-shrink-0"
                  />
                  <span className="text-sm text-[#6B7280] leading-relaxed">
                    {t('flex.landing.privacyText')}{' '}
                    <a href="https://www.weship.eu/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[#338edc] underline">
                      {t('flex.landing.privacyLink')}
                    </a>{' '}
                    *
                  </span>
                </label>
                {errors.datenschutz && <p className="mt-1 text-xs text-red-500">{errors.datenschutz}</p>}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 bg-[#338edc] hover:bg-[#2b7bc4] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-lg py-4 rounded-2xl transition-colors"
              >
                {submitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>{t('flex.landing.navApply')}</>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="py-10 bg-[#1A1A2E] text-white/70">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center space-y-4">
          <h3 className="text-lg font-semibold text-white">{t('flex.landing.footerTitle')}</h3>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
            <a href="tel:+43316375093" className="inline-flex items-center gap-2 hover:text-white transition-colors">
              <Phone size={18} /> +43 316 375 093
            </a>
          </div>
          <div className="pt-4 border-t border-white/10 text-sm">
            <p>&copy; {new Date().getFullYear()} We Ship Fulfillment GmbH &middot; <a href="https://www.weship.eu" target="_blank" rel="noopener noreferrer" className="hover:text-white">www.weship.eu</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
